import { formatAssociatedPartyAddress } from '@/features/associated-parties/formatAddress'
import type { AssociatedParty } from '@/shared/hooks/api/associatedParties'

export function formatCompliancePdfDate(iso: string, locale: string): string {
  try {
    const date = new Date(iso)
    if (Number.isNaN(date.getTime())) {
      return iso
    }
    return date.toLocaleDateString(locale, { dateStyle: 'long' })
  } catch {
    return iso
  }
}

function escapeHtmlForPdfCell(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

export type TranslateAssociatedPartyFn = (
  key: string,
  options?: Record<string, unknown>
) => string

export type CompliancePdfCoverLabels = {
  manufacturer: string
  productName: string
  model: string
  year: string
  documentCreated: string
  notAvailable: string
}

export type BuildCompliancePdfCoverMarkdownInput = {
  documentTitle: string
  labels: CompliancePdfCoverLabels
  manufacturerParty: AssociatedParty | null
  getCountryName: (countryCode: string) => string
  translateAssociatedParty: TranslateAssociatedPartyFn
  productName: string
  model: string
  year: string
  /** Prefer `generatedAt`, fall back to `createdAt` (ISO strings). */
  documentTimestampIso: string
  formatDate: (iso: string) => string
}

function buildManufacturerCellInnerHtml(
  party: AssociatedParty | null,
  notAvailable: string,
  getCountryName: (countryCode: string) => string,
  translateAssociatedParty: TranslateAssociatedPartyFn
): string {
  if (party == null) {
    return escapeHtmlForPdfCell(notAvailable)
  }
  const nameTrimmed = party.name.trim()
  const displayName = nameTrimmed !== '' ? nameTrimmed : notAvailable
  const address = formatAssociatedPartyAddress(party, {
    getCountryName,
    translate: translateAssociatedParty,
  })
  const emptyMarker = translateAssociatedParty('emptyValue')
  const addressTrimmed = address.trim()
  if (addressTrimmed === '' || addressTrimmed === emptyMarker) {
    return escapeHtmlForPdfCell(displayName)
  }
  return `${escapeHtmlForPdfCell(displayName)}<br><br>${escapeHtmlForPdfCell(addressTrimmed)}`
}

function metaTableRow(label: string, valueInnerHtml: string): string {
  return `<tr><td class="pdf-cover-meta-label">${escapeHtmlForPdfCell(label)}</td><td class="pdf-cover-meta-value">${valueInnerHtml}</td></tr>`
}

/**
 * First page of exported compliance PDFs (risk assessment, instruction manual).
 * Manufacturer comes from the MANUFACTURER Associated Party (name + formatted address).
 * Metadata is an HTML table (no header row), centered via {@link getPdfCoverMarkdownCss}.
 */
export function buildCompliancePdfCoverMarkdown(
  input: BuildCompliancePdfCoverMarkdownInput
): string {
  const manufacturerInner = buildManufacturerCellInnerHtml(
    input.manufacturerParty,
    input.labels.notAvailable,
    input.getCountryName,
    input.translateAssociatedParty
  )

  const productNameDisplay =
    input.productName.trim() !== '' ? input.productName.trim() : input.labels.notAvailable
  const modelDisplay =
    input.model.trim() !== '' ? input.model.trim() : input.labels.notAvailable
  const yearDisplay =
    input.year.trim() !== '' ? input.year.trim() : input.labels.notAvailable

  const dateFormatted = input.formatDate(input.documentTimestampIso)

  const rows = [
    metaTableRow(input.labels.manufacturer, manufacturerInner),
    metaTableRow(
      input.labels.productName,
      escapeHtmlForPdfCell(productNameDisplay)
    ),
    metaTableRow(input.labels.model, escapeHtmlForPdfCell(modelDisplay)),
    metaTableRow(input.labels.year, escapeHtmlForPdfCell(yearDisplay)),
    metaTableRow(
      input.labels.documentCreated,
      escapeHtmlForPdfCell(dateFormatted)
    ),
  ].join('\n')

  const table = `<div class="pdf-cover-meta-table-wrap">
<table class="pdf-cover-meta-table">
<tbody>
${rows}
</tbody>
</table>
</div>`

  return [`# ${input.documentTitle}`, '', table].join('\n')
}
